/**
 * Tailwind CSS implementation of the v2 Table sub-components.
 * Uses Seeds table CSS classes from table-v2.css.
 *
 * These are the pure semantic-HTML + CSS-class leaves the hybrid wrappers render
 * on the fast path. They forward `className` and `ref` so `styled(Component)`
 * extensions keep working, and pass standard HTML attributes (scope, id, role,
 * tabIndex, …) straight through to the DOM. Every leaf honors the polymorphic
 * `as` prop (rendering a dynamic element instead of a hard-coded tag) so
 * `<TableCell as="th" scope="row">` produces a real `<th scope="row">` with the
 * `rowheader` role and `<TableRow as="div">` renders a `<div>`, matching the
 * styled-components path. Because `as` is destructured out, it never leaks onto
 * the DOM node; a component-valued `as` (e.g. `styled(TableRow)`) re-enters the
 * hybrid, which then detects the styled signal and routes to styled-components.
 */

import React from "react";

// Utility for merging class names properly
function cn(
  ...inputs: (string | undefined | null | false | Record<string, boolean>)[]
): string {
  const classes: string[] = [];

  for (const input of inputs) {
    if (!input) continue;

    if (typeof input === "string") {
      classes.push(input);
    } else if (typeof input === "object") {
      for (const [key, value] of Object.entries(input)) {
        if (value) {
          classes.push(key);
        }
      }
    }
  }

  return classes.join(" ");
}

type TailwindProps = {
  className?: string;
  children?: React.ReactNode;
  [key: string]: any;
};

export const TableV2ContainerTailwind = React.forwardRef<
  HTMLDivElement,
  TailwindProps
>(({ className, children, as: Component = "div", ...rest }, ref) => (
  <Component
    ref={ref}
    className={cn("seeds-table-v2-container", className)}
    {...rest}
  >
    {children}
  </Component>
));

TableV2ContainerTailwind.displayName = "TableV2.ContainerTailwind";

export const TableRootTailwind = React.forwardRef<
  HTMLTableElement,
  TailwindProps
>(({ className, children, as: Component = "table", ...rest }, ref) => (
  <Component ref={ref} className={cn("seeds-table-v2", className)} {...rest}>
    {children}
  </Component>
));

TableRootTailwind.displayName = "TableV2.TableTailwind";

export const CaptionTailwind = React.forwardRef<
  HTMLTableCaptionElement,
  TailwindProps & { isVisible?: boolean }
>(
  (
    {
      className,
      children,
      isVisible = true,
      as: Component = "caption",
      ...rest
    },
    ref
  ) => (
    <Component
      ref={ref}
      className={cn(
        "seeds-table-v2-caption",
        { "seeds-table-v2-caption-hidden": !isVisible },
        className
      )}
      {...rest}
    >
      {children}
    </Component>
  )
);

CaptionTailwind.displayName = "TableV2.CaptionTailwind";

export const TableHeaderTailwind = React.forwardRef<
  HTMLTableSectionElement,
  TailwindProps
>(({ className, children, as: Component = "thead", ...rest }, ref) => (
  <Component
    ref={ref}
    className={cn("seeds-table-v2-header", className)}
    {...rest}
  >
    {children}
  </Component>
));

TableHeaderTailwind.displayName = "TableV2.TableHeaderTailwind";

export const TableBodyTailwind = React.forwardRef<
  HTMLTableSectionElement,
  TailwindProps
>(({ className, children, as: Component = "tbody", ...rest }, ref) => (
  <Component
    ref={ref}
    className={cn("seeds-table-v2-body", className)}
    {...rest}
  >
    {children}
  </Component>
));

TableBodyTailwind.displayName = "TableV2.TableBodyTailwind";

export const TableRowTailwind = React.forwardRef<
  HTMLTableRowElement,
  TailwindProps
>(({ className, children, as: Component = "tr", ...rest }, ref) => (
  <Component
    ref={ref}
    className={cn("seeds-table-v2-row", className)}
    {...rest}
  >
    {children}
  </Component>
));

TableRowTailwind.displayName = "TableV2.TableRowTailwind";

export const TableHeaderCellTailwind = React.forwardRef<
  HTMLTableCellElement,
  TailwindProps
>(({ className, children, as: Component = "th", ...rest }, ref) => (
  <Component
    ref={ref}
    className={cn("seeds-table-v2-header-cell", className)}
    {...rest}
  >
    {children}
  </Component>
));

TableHeaderCellTailwind.displayName = "TableV2.TableHeaderCellTailwind";

export const TableCellTailwind = React.forwardRef<
  HTMLTableCellElement,
  TailwindProps
>(({ className, children, as: Component = "td", ...rest }, ref) => (
  <Component
    ref={ref}
    className={cn("seeds-table-v2-cell", className)}
    {...rest}
  >
    {children}
  </Component>
));

TableCellTailwind.displayName = "TableV2.TableCellTailwind";
